import { getApiBaseUrl } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type {
  PayslipDocumentDto,
  PayslipListQuery,
  PayslipListResult,
  PayslipPdfDownload,
  PayslipPublishResult,
} from './payslipsApiTypes';

/**
 * PRR-356. Typed client for the pay run payslip endpoints. Every message carried by `PayslipRequestError` is curated
 * here: the server's own `message` is never surfaced, because it must not be trusted to be free of identifiers.
 * Unknown responses are validated at this boundary and re-built field by field, so nothing unexpected (an object key,
 * a bucket name) can travel any further into the UI.
 */
export class PayslipRequestError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = 'PayslipRequestError';
  }
}

const GENERIC_MESSAGE = 'Something went wrong. Try again.';
const INVALID_RESPONSE_MESSAGE = 'The server returned an unexpected response. Try again.';
const DEFAULT_FILE_NAME = 'payslip.pdf';

const MESSAGE_BY_CODE: Readonly<Record<string, string>> = {
  payslip_storage_unavailable: 'Payslip storage is temporarily unavailable. Try again shortly.',
  payslip_audit_unavailable: 'The change could not be recorded, so nothing was changed. Try again shortly.',
  payslip_none_for_pay_run: 'No payslips have been stored for this pay run yet.',
  payslip_integrity_failed: 'This payslip does not match its approved content, so it was not released. Ask for it to be stored again.',
  payrun_not_found: 'This pay run or payslip could not be found.',
  payrun_validation_failed: 'The request was not valid. Refresh and try again.',
  payrun_unauthorized: 'Your session has expired. Sign in again.',
};

const MESSAGE_BY_STATUS: Readonly<Record<number, string>> = {
  400: MESSAGE_BY_CODE.payrun_validation_failed,
  401: MESSAGE_BY_CODE.payrun_unauthorized,
  403: 'You do not have permission to manage payslips.',
  404: MESSAGE_BY_CODE.payrun_not_found,
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const invalid = (status: number) => new PayslipRequestError(status, 'payslip_invalid_response', INVALID_RESPONSE_MESSAGE);

const scopeUrl = (businessRecordId: string, payId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/payrun/${encodeURIComponent(payId)}/payslips`;

const readCode = (body: unknown): string | null => {
  if (!isRecord(body)) return null;
  const fromResult = isRecord(body.result) && typeof body.result.code === 'string' ? body.result.code : null;
  const fromError = isRecord(body.error) && typeof body.error.code === 'string' ? body.error.code : null;
  return (fromResult ?? fromError)?.toLowerCase() ?? null;
};

const toRequestError = async (response: Response): Promise<PayslipRequestError> => {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A non-JSON failure body is never read, shown or treated as content.
  }
  const code = readCode(body);
  const message = (code ? MESSAGE_BY_CODE[code] : undefined) ?? MESSAGE_BY_STATUS[response.status] ?? GENERIC_MESSAGE;
  return new PayslipRequestError(response.status, code ?? (response.status === 403 ? 'payrun_forbidden' : 'payslip_request_failed'), message);
};

const send = async (url: string, init?: RequestInit, mutating = false): Promise<Response> => {
  try {
    return await (mutating ? authFetch(url, init, { retryUnauthorized: false }) : authFetch(url, init));
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new PayslipRequestError(0, 'payslip_network_error', GENERIC_MESSAGE);
  }
};

const unwrap = async (response: Response): Promise<unknown> => {
  if (!response.ok) throw await toRequestError(response);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw invalid(response.status);
  }
  if (!isRecord(body) || body.success === false || !isRecord(body.result)) throw invalid(response.status);
  return body.result;
};

const toPayslip = (value: unknown, status: number): PayslipDocumentDto => {
  if (!isRecord(value)) throw invalid(status);
  const { id, organisationId, businessRecordId, financialYear, payGroupId, hrPersonId, payRunId, paymentDate, contentLength, isPublished, publishedAt, createdAt } = value;
  if (typeof id !== 'string' || typeof organisationId !== 'string' || typeof businessRecordId !== 'string' || typeof financialYear !== 'string'
    || typeof payGroupId !== 'string' || typeof hrPersonId !== 'string' || typeof payRunId !== 'string' || typeof paymentDate !== 'string'
    || !isNumber(contentLength) || typeof isPublished !== 'boolean' || typeof createdAt !== 'string'
    || !(publishedAt === null || publishedAt === undefined || typeof publishedAt === 'string')) throw invalid(status);
  return {
    id, organisationId, businessRecordId, financialYear, payGroupId, hrPersonId, payRunId, paymentDate, contentLength, isPublished,
    publishedAt: publishedAt ?? null, createdAt,
  };
};

const toPublishResult = (value: unknown, status: number): PayslipPublishResult => {
  if (!isRecord(value)) throw invalid(status);
  const { payRunId, published, total, changed } = value;
  if (typeof payRunId !== 'string' || typeof published !== 'boolean' || !isNumber(total) || !isNumber(changed)) throw invalid(status);
  return { payRunId, published, total, changed };
};

export const listPayRunPayslips = async (
  businessRecordId: string,
  payId: string,
  query: PayslipListQuery,
  signal?: AbortSignal,
): Promise<PayslipListResult> => {
  const params = new URLSearchParams();
  if (query.published !== undefined) params.set('published', String(query.published));
  params.set('skip', String(query.skip));
  params.set('take', String(query.take));
  const response = await send(`${scopeUrl(businessRecordId, payId)}?${params}`, { signal });
  const result = await unwrap(response);
  if (!isRecord(result) || !Array.isArray(result.items) || !isNumber(result.total) || !isNumber(result.skip) || !isNumber(result.take)) throw invalid(response.status);
  return { items: result.items.map((item) => toPayslip(item, response.status)), total: result.total, skip: result.skip, take: result.take };
};

/** Safe save name: only `payslip-*.pdf` built from letters, digits and hyphens is accepted, otherwise a fixed default. */
export const payslipFileNameFrom = (response: Response): string => {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const quoted = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const bare = /filename=([^;]+)/i.exec(disposition)?.[1]?.trim();
  const candidate = (quoted ?? bare ?? '').trim();
  const name = candidate.split(/[\\/]/).pop() ?? '';
  return /^payslip-[A-Za-z0-9-]+\.pdf$/.test(name) ? name : DEFAULT_FILE_NAME;
};

/**
 * The success body is the PDF itself, not the JSON envelope, so this bypasses `unwrap`. Failures arrive as the ordinary
 * JSON error contract. A 200 that is not `application/pdf` is refused rather than saved under a .pdf name.
 */
export const downloadPayslipPdf = async (businessRecordId: string, payId: string, hrPersonId: string): Promise<PayslipPdfDownload> => {
  const response = await send(`${scopeUrl(businessRecordId, payId)}/${encodeURIComponent(hrPersonId)}/pdf`);
  if (!response.ok) throw await toRequestError(response);
  if (!(response.headers.get('Content-Type') ?? '').toLowerCase().startsWith('application/pdf')) throw invalid(response.status);
  return { blob: await response.blob(), fileName: payslipFileNameFrom(response) };
};

const setPayslipPublished = async (businessRecordId: string, payId: string, hrPersonId: string, action: 'publish' | 'unpublish') => {
  const response = await send(`${scopeUrl(businessRecordId, payId)}/${encodeURIComponent(hrPersonId)}/${action}`, { method: 'POST' }, true);
  return toPayslip(await unwrap(response), response.status);
};

const setPayRunPublished = async (businessRecordId: string, payId: string, action: 'publish' | 'unpublish') => {
  const response = await send(`${scopeUrl(businessRecordId, payId)}/${action}`, { method: 'POST' }, true);
  return toPublishResult(await unwrap(response), response.status);
};

export const publishPayslip = (businessRecordId: string, payId: string, hrPersonId: string) => setPayslipPublished(businessRecordId, payId, hrPersonId, 'publish');
export const unpublishPayslip = (businessRecordId: string, payId: string, hrPersonId: string) => setPayslipPublished(businessRecordId, payId, hrPersonId, 'unpublish');
export const publishPayRunPayslips = (businessRecordId: string, payId: string) => setPayRunPublished(businessRecordId, payId, 'publish');
export const unpublishPayRunPayslips = (businessRecordId: string, payId: string) => setPayRunPublished(businessRecordId, payId, 'unpublish');
